import uuidv5 from 'modules/utils/uuid';
import { Color } from 'modules/styles/colors';
import {
  TriagePatient,
  TriageProxy,
  TriageCondition,
  TriageEvidence
} from 'store/triageGyant/types';
import { Consumer } from 'store/profile/types';

export type PhoneType = 'emergency' | 'covid' | 'emotionalSupport' | 'nurseLine';

export const getPhoneDetails = (phoneType: PhoneType) => {
  switch (phoneType) {
    case 'emergency':
      return { title: 'Your Local Emergency Hotline', number: '911' };
    case 'covid':
      return {
        title: 'COVID-19 Call Center',
        number: '(844) 442-5224'
      };
    case 'emotionalSupport':
      return {
        title: 'Emotional Health Relief Hotline',
        number: '(833) 442-2211'
      };
    case 'nurseLine':
      return {
        title: 'Chat with a Nurse for free',
        number: '(844) 442-6618'
      };
    default:
      return {};
  }
};

export const getLikelihoodText = (score: number) => {
  switch (score) {
    case 5:
    case 4:
      return 'Most Likely';
    case 3:
      return 'Likely';
    case 2:
    case 1:
      return 'Possible';
    default:
      return 'Possible';
  }
};

export const getCareBadgeColor = (text: string) => {
  switch (text) {
    case 'BEST':
      return Color.secondary;
    case 'GOOD':
      return Color.grayHue2;
    case 'FAIR':
      return Color.grayHue5;
    case 'LOW':
      return Color.grayHue4;
    default:
      return undefined;
  }
};

export const getCareBadgeText = (itemIndex: number) => {
  switch (itemIndex) {
    case 0:
      return 'BEST';
    case 1:
    case 2:
      return 'GOOD';
    case 3:
    case 4:
      return 'FAIR';
    default:
      return 'LOW';
  }
};

export const getCareDisplayText = (title: string) => {
  switch (title) {
    case 'selfCareAtHome':
      return 'Self Care at Home';
    case 'asyncTelemedicine':
      return 'E-Visit';
    case 'realTimeVideo':
      return 'Connect Care';
    case 'primaryCarePhysician':
      return 'Primary Care Physician';
    case 'urgentCare':
      return 'Urgent Care';
    case 'childEmergencyRoom':
      return "Primary Children's Hospital Emergency Department";
    case 'emergencyRoom':
      return 'Emergency Room';
    case 'call911':
      return 'CALL 911';
    case 'covidCallCenter':
      return 'COVID-19 Testing';
    case 'emotionalSupport':
      return 'Emotional Health Relief Hotline';
    case 'nurseLine':
      return 'Health Answers';
    default:
      return '';
  }
};

export const getCareDescription = (title: string) => {
  switch (title) {
    case 'selfCareAtHome':
      return 'If you feel like you need further help with your symptoms, speak to a provider through Connect Care.';
    case 'asyncTelemedicine':
      return 'Quickly share how you are feeling with a provider by answering a few questions to receive your treatment plan, without needing to leave your home, during the time that works best for you.';
    case 'realTimeVideo':
      return 'Experiencing symptoms but not sure what care you need? Access an Intermountain Provider via Connect Care to get the care you need, quickly and conveniently, from wherever you are.';
    case 'primaryCarePhysician':
      return 'Schedule an appointment today with an Intermountain provider.';
    case 'urgentCare':
      return 'Find availability at an urgent care center near you for non-emergency medical care.';
    case 'childEmergencyRoom':
      return '';
    case 'emergencyRoom':
      return '';
    case 'call911':
      return '';
    case 'covidCallCenter':
      return '';
    case 'emotionalSupport':
      return '';
    case 'nurseLine':
      return 'Call the 24-hour nurse line to address medical concerns and be directed to the proper level of care.';
    default:
      return null;
  }
};

export const getButtonText = (title: string) => {
  switch (title) {
    case 'selfCareAtHome':
      return {
        extraText: `Still want to see a doctor?
        Try Connect Care`,
        buttonText: 'See Doctor'
      };
    case 'asyncTelemedicine':
      return {
        extraText: '',
        buttonText: 'Learn More'
      };
    case 'realTimeVideo':
      return {
        extraText: '',
        buttonText: 'Get Care'
      };
    case 'primaryCarePhysician':
      return {
        extraText: '',
        buttonText: 'Book an Appointment'
      };
    case 'urgentCare':
      return {
        extraText: '',
        buttonText: 'Facility Search'
      };
    case 'childEmergencyRoom':
      return {
        extraText: '',
        buttonText: 'Get Details'
      };
    case 'emergencyRoom':
      return {
        extraText: 'Find nearest emergency room',
        buttonText: 'Find ER'
      };
    case 'call911':
      return {
        extraText: '',
        buttonText: 'Call 911'
      };
    case 'covidCallCenter':
      return {
        extraText: '',
        buttonText: 'Request a test'
      };
    case 'emotionalSupport':
      return {
        extraText: 'Linking callers to supportive resources, tools, and treatments',
        buttonText: 'Find Resources'
      };
    case 'nurseLine':
      return {
        extraText: 'Have more questions? Chat with a Nurse for free',
        buttonText: 'Chat with Nurse'
      };
    default:
      return {
        extraText: '',
        buttonText: ''
      };
  }
};

export const getEndpointRank = (title: string) => {
  switch (title) {
    case 'selfCareAtHome':
      return 1;
    case 'emotionalSupport':
      return 2;
    case 'nurseLine':
      return 3;
    case 'covidCallCenter':
      return 4;
    case 'asyncTelemedicine':
      return 5;
    case 'realTimeVideo':
      return 6;
    case 'primaryCarePhysician':
      return 7;
    case 'urgentCare':
      return 8;
    case 'childEmergencyRoom':
      return 9;
    case 'emergencyRoom':
      return 10;
    case 'call911':
      return 11;
    default:
      return null;
  }
};

interface EncryptionSettings {
  password: string;
  algorithm: string;
}

interface CareOption {
  title: string;
  badgeText: string;
}

interface MappedCareOption {
  careOption: string;
  careOptionRating: string;
}

interface MappedCondition {
  cause: string;
}

const uuidPatientId = (consumerId: string) => {
  const BASE_NAMESPACE = '929c7487-888b-47cb-95af-95ce15e746ec'; // Needed to generate consistent Ids for a given input
  return uuidv5(consumerId, BASE_NAMESPACE);
};

export interface TriageConsumerData extends Consumer {
  guarantorId?: string;
  isProxy: boolean;
  relationship?: string;
}

export interface PatientInfoOptions {
  insuranceUpdated?: boolean;
  selfPay?: boolean;
}

export const buildPatientDTO = (
  consumer: TriageConsumerData,
  conditionCode?: string,
  options?: PatientInfoOptions
) => {
  let patientInfo: undefined | TriagePatient | TriageProxy;
  if (consumer.consumerId) {
    if (!consumer.relationship || !consumer.isProxy) {
      patientInfo = {
        patientId: uuidPatientId(consumer.consumerId),
        firstName: consumer.firstName,
        lastName: consumer.lastName,
        dateOfBirth: consumer.dateOfBirth,
        gender: consumer.gender,
        relationship: 'Self',
        ...options
      };
    } else {
      patientInfo = {
        grantor: {
          patientId: uuidPatientId(consumer.consumerId),
          proxyPatientId: consumer.guarantorId ? uuidPatientId(consumer.guarantorId) : undefined,
          firstName: consumer.firstName,
          lastName: consumer.lastName,
          dateOfBirth: consumer.dateOfBirth,
          gender: consumer.gender,
          relationship: 'Other'
        },
        ...options
      };
    }
  }

  if (conditionCode && patientInfo) {
    patientInfo.patientSuspectedConditions = [conditionCode];
  }

  return patientInfo;
};

export const getEnteredSymptoms = (symptoms: TriageEvidence[]): TriageEvidence[] =>
  symptoms.filter(symptom => symptom.status === 'present');

export const getCareOptionsPresented = (careOptions: CareOption[]): MappedCareOption[] =>
  careOptions.map(careOption => ({
    careOption: careOption.title,
    careOptionRating: careOption.badgeText
  }));

export const getTopThreeCauses = (conditions: TriageCondition[]): MappedCondition[] =>
  conditions.slice(0, 3).map((condition: TriageCondition) => ({
    cause: condition.conditionText,
    probabilityPercent: condition.likelihoodScore * 20 // Gyant gives 1 through 5, convert to percent
  }));
